import React, { useMemo, useState } from "react";
import dayjs from "dayjs";

import {
	useGetDeviceStatusActivityQuery,
	GetDeviceStatusActivityQuery
} from "graphql/queries/get-device-status-activity.generated";

type ActivityItem = NonNullable<NonNullable<GetDeviceStatusActivityQuery["get_device_status_activity"]>[number]>;

const BASE = process.env.PUBLIC_URL;
const CLOCK_ICON = `${BASE}/assets/images/icons/clock-small.svg`;
const VISIBLE_COUNT = 10;

const DEVICE_ACTIVITY_CONTENT: Record<string, { icon: string; label: string; description: string }> = {
	online: {
		icon: `${BASE}/assets/images/icons/device-activity-online.svg`,
		label: "Online",
		description: "Device connected and stable."
	},
	offline: {
		icon: `${BASE}/assets/images/icons/device-activity-offline.svg`,
		label: "Offline",
		description: "Device disconnected."
	},
	installed: {
		icon: `${BASE}/assets/images/icons/device-activity-installed.svg`,
		label: "Installed",
		description: "Device was installed and initialized."
	}
};

interface DeviceStatusActivityProps {
	installedProductId?: string;
}

const DeviceStatusActivity: React.FC<DeviceStatusActivityProps> = ({ installedProductId }) => {
	const [isExpanded, setIsExpanded] = useState(false);

	const { data } = useGetDeviceStatusActivityQuery({
		skip: !installedProductId,
		variables: { installedProductId: installedProductId! }
	});

	const activities = useMemo(
		() => (data?.get_device_status_activity?.filter(Boolean) as ActivityItem[]) ?? [],
		[data]
	);

	const visibleActivities = isExpanded ? activities : activities.slice(0, VISIBLE_COUNT);
	const hiddenCount = activities.length - VISIBLE_COUNT;

	if (!activities.length) return null;

	return (
		<div className="dsa-section">
			<div className="dsa-card">
				<div className="dsa-title">Activity</div>
				{visibleActivities.map((item, index) => {
					const content = DEVICE_ACTIVITY_CONTENT[item.event_state];
					if (!content) return null;

					const isLastVisible = index === visibleActivities.length - 1;
					const timestamp =
						item.event_state === "installed"
							? dayjs(item.event_timestamp).format("MM/DD/YYYY")
							: dayjs(item.event_timestamp).format("MM/DD/YYYY, h:mm a");

					return (
						<div key={item.id} className="dsa-row">
							<div className="dsa-icon-col">
								<img className="dsa-icon" src={content.icon} alt="" />
								{!isLastVisible && <div className="dsa-line" />}
							</div>
							<div className="dsa-content">
								<div className="dsa-text">
									<span className="dsa-label">{content.label}</span>
									<span className="dsa-description">{content.description}</span>
								</div>
								<span className="dsa-timestamp">
									<img src={CLOCK_ICON} alt="" />
									{timestamp}
								</span>
							</div>
						</div>
					);
				})}

				{hiddenCount > 0 && (
					<button className="dsa-view-more" onClick={() => setIsExpanded(prev => !prev)}>
						{isExpanded ? "View less" : `View ${hiddenCount} more events`}
					</button>
				)}
			</div>
		</div>
	);
};

export default DeviceStatusActivity;
